import { supabase, useSupabaseFunction } from '@/services/supabase';
import { Property, PropertyUpdate } from '@/types/property';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useAuth } from '../useAuth';

export function usePropertyQueries() {
    const queryClient = useQueryClient();
    const discordNotify = useSupabaseFunction("discord-notify");
    const { profile } = useAuth();

    // Fetching functions
    const fetchProperty = async (propertyId: string) => {
        const { data, error } = await supabase
            .from('properties')
            .select('*, rooms:rooms(*), images:property_images(*)')
            .eq('property_id', propertyId)
            .single();
        if (error) throw error;
        return data as Property;
    };

    const fetchActiveProperties = async () => {
        const { data, error } = await supabase
            .from('properties')
            .select('*, rooms:rooms(*), images:property_images(*)')
            .in('status', ['active', 'archive']);
        if (error) throw error;
        return data as Property[];
    };

    const fetchUserProperties = async (userId: string) => {
        const { data, error } = await supabase
            .from('properties')
            .select('*, rooms:rooms(*), images:property_images(*)')
            .eq('user_id', userId);
        if (error) throw error;
        return data as Property[];
    };

    const createProperty = async () => {
        const { data, error } = await supabase
            .from('properties')
            .insert({})
            .select('*')
            .single();

        if (error) throw error;

        discordNotify.mutateAsync({
            message: `Property created`,
            data: {
                propertyId: data.property_id,
                userId: profile?.user_id,
                email: profile?.email,
            }
        });

        return { ...data, rooms: [] };
    };

    // Mutation functions
    const upsertProperty = async ({
        propertyId,
        data
    }: {
        propertyId: string,
        data: PropertyUpdate
    }) => {
        const { rooms, ...propertyData } = data;

        const { error: propertyError } = await supabase
            .from('properties')
            .upsert({
                ...propertyData,
                property_id: propertyId,
                possession_date: propertyData.possession_date as string
            });

        if (propertyError) throw propertyError;

        // 2. Get existing rooms to determine which ones to delete
        const { data: existingRooms, error: fetchError } = await supabase
            .from('rooms')
            .select('room_id')
            .eq('property_id', propertyId);

        if (fetchError) throw fetchError;

        if (!rooms) {
            return { ...propertyData, property_id: propertyId, rooms: existingRooms };
        }

        const existingRoomIds = new Set(existingRooms?.map(r => r.room_id) || []);
        const newRoomIds = new Set(rooms.map(r => r.room_id));

        const roomIdsToDelete = [...existingRoomIds].filter(id => !newRoomIds.has(id));

        const roomsToUpsert = rooms.map(room => ({
            ...room,
            property_id: propertyId
        }));

        // 4. Upsert rooms
        if (roomsToUpsert.length > 0) {
            const { error: upsertError } = await supabase
                .from('rooms')
                .upsert(roomsToUpsert, {
                    onConflict: 'room_id',
                    ignoreDuplicates: false
                });

            if (upsertError) throw upsertError;
        }

        // 5. Delete removed rooms
        if (roomIdsToDelete.length > 0) {
            const { error: deleteError } = await supabase
                .from('rooms')
                .delete()
                .in('room_id', roomIdsToDelete);

            if (deleteError) throw deleteError;
        }

        return { ...propertyData, property_id: propertyId, rooms };
    };

    const deleteProperty = async ({ propertyId }: { propertyId: string }) => {
        // Delete the property (cascade will handle rooms)
        const { error } = await supabase
            .from('properties')
            .delete()
            .eq('property_id', propertyId);

        if (error) throw error;
        return { propertyId };
    };

    return {
        // Query hooks
        useFetchProperty: (propertyId: string) => useSuspenseQuery({
            queryKey: ['property', propertyId],
            queryFn: () => fetchProperty(propertyId),
        }),

        useFetchActiveProperties: () => useSuspenseQuery({
            queryKey: ['properties', 'active'],
            queryFn: fetchActiveProperties
        }),

        useFetchUserProperties: (userId: string) => useSuspenseQuery({
            queryKey: ['properties', 'user', userId],
            queryFn: async () => {
                const properties = await fetchUserProperties(userId);

                properties.forEach((property) => {
                    queryClient.setQueryData(['property', property.property_id], property);
                });

                return properties;
            },
        }),

        // New personal property query hook
        createPropertyMutation: useMutation({
            mutationFn: async () => await createProperty(),
            onSuccess: (data) => {
                queryClient.setQueryData(['property', data.property_id], data);

                const userId = data.user_id;
                const existingUserProperties = queryClient.getQueryData(['properties', 'user', userId]) as Property[] | undefined;

                if (existingUserProperties) {
                    queryClient.setQueryData(
                        ['properties', 'user', userId],
                        [...existingUserProperties, data]
                    );
                } else {
                    queryClient.invalidateQueries({ queryKey: ['properties', 'user', userId] });
                }
            }
        }),

        // Mutation hooks
        upsertPropertyMutation: useMutation({
            mutationFn: upsertProperty,
            onSuccess: (data) => {
                queryClient.setQueryData(['property', data.property_id], data);

                const userId = data.user_id;
                const existingUserProperties = queryClient.getQueryData(['properties', 'user', userId]) as Property[] | undefined;

                if (existingUserProperties) {
                    queryClient.setQueryData(
                        ['properties', 'user', userId],
                        existingUserProperties.map(p => p.property_id === data.property_id ? data : p
                        )
                    );
                } else {
                    queryClient.invalidateQueries({ queryKey: ['properties', 'user', userId] });
                }
            }
        }),

        deletePropertyMutation: useMutation({
            mutationFn: deleteProperty,
            onSuccess: ({ propertyId }) => {
                const cacheData = queryClient.getQueryData(['property', propertyId]) as Property | undefined;

                if (cacheData) {
                    const userId = cacheData.user_id;
                    const existingUserProperties = queryClient.getQueryData(['properties', 'user', userId]) as Property[] | undefined;

                    if (existingUserProperties) {
                        queryClient.setQueryData(
                            ['properties', 'user', userId],
                            existingUserProperties.filter(p => p.property_id !== propertyId)
                        );
                    }
                }
                queryClient.removeQueries({ queryKey: ['property', propertyId] });
            }
        }),
    };
}